<template>
  <div
    class="modal text-white space-y-5 m-auto max-w-[1248px] w-full bg-black rounded-lg overflow-hidden"
  >
    <iframe
      :src="videoSource"
      width="100%"
      height="620"
      frameborder="0"
      allowfullscreen
    />
    <section class="px-10 pb-10">
      <div
        class="actions md:flex justify-between flex-row-reverse space-y-4 md:space-y-0"
      >
        <div class="space-x-4 flex flex-nowrap items-center max-h-fit">
          <Button
            v-if="authStore.user"
            size="xs"
            :type="
              video?.reactions.personal.includes('lovely') ? 'green' : 'gray'
            "
            :icon="IconHeart"
            :title="video?.reactions?.global?.lovely"
            class="flex text-sm gap-2"
            @click="sendReaction('lovely')"
          />
          <Button
            size="xs"
            type="gray"
            title="Поделиться"
            :icon="IconShare3"
            class="flex gap-2 text-sm"
            @click="openShareModal(href)"
          />
          <Button
            v-if="origin"
            size="xs"
            type="gray"
            title="Скачать"
            :icon="IconDownload"
            class="flex gap-2 text-sm"
            @click="downloadFile(origin)"
          />
        </div>
        <div class="space-y-3 max-w-[768px]">
          <div class="space-y-3">
            <p class="text-xl" v-text="video?.name"></p>
            <p class="text-sm" v-html="video?.description"></p>
          </div>
          <a
            href="/docs/video-regulations.pdf"
            target="_blank"
            class="text-green-300 font-semibold flex gap-2 items-center hover:underline"
          >
            <p>Полные условия использования</p>
            <IconPaper />
          </a>
        </div>
      </div>
      <div class="comments space-y-3">
        <Comments
          class="mt-12"
          root="album-videos"
          :id="videoId"
          :dependant="!!authStore.user"
          :endpoint="`/albums/${albumId}/videos/${videoId}/comments`"
          :sendEndpointOverwrite="`/albums/${albumId}/videos/${videoId}/comment`"
        />
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useHead } from 'unhead'
import { computed, inject, onMounted, onUnmounted, ref } from 'vue'
import { useQuery } from 'vue-query'
import { useRoute, useRouter } from 'vue-router'

import { useAuthStore } from '@/store/auth.js'

import { IconDownload, IconHeart, IconShare3 } from '@tabler/icons-vue'

import Comments from '@/components/comments/Comments.vue'
import IconPaper from '@/components/icons/IconPaper.vue'
import Button from '@/components/ui/UIButton.vue'
import useModalDynamic from '@/plugins/useModalDynamic.js'
import { isObject } from '@/utils/isObject'
import { useReactions } from '@/utils/reactions.js'

import { useFileDownloader } from '../../utils/use/useFileDownload'
import ShareModal from './ShareModal.vue'

interface Props {
  videoId: string | number
  albumId: string | number
  returnUrl: string
}

const props = defineProps<Props>()
const http = inject('http')
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const queryKey = ['albums', props.videoId]
let meta = null

const { isDownloading, error, downloadFile } = useFileDownloader()
const resource = ref(null)
const href = ref('')
const videoSource = ref(``)

function getFirstOrigin(resource) {
  if (!resource?.origins) return undefined
  return isObject(resource.origins)
    ? Object.values(resource.origins)[0]
    : resource.origins[0]
}

const origin = computed(() => getFirstOrigin(resource.value))

const { setReaction } = useReactions({
  auth: useAuthStore(),
  queryKey,
  rootKey: 'album-videos',
  rootParamId: props.videoId,
})

const loadSource = (external_id) => {
  if (!external_id) return
  videoSource.value = `https://video-cdn.gov-murman.ru/${external_id}`
}

const { data: video } = useQuery({
  queryKey,
  queryFn: () => http(`/albums/${props.albumId}/videos/${props.videoId}`),
  select: (data) => {
    loadSource(data.resource?.external_id)
    return data.resource
  },
})

const openShareModal = (link: string) => {
  useModalDynamic({
    opened: true,
    component: ShareModal,
    compAttrs: {
      context: {
        link,
      },
    },
  })
}

const clearRouteParams = () => {
  if (!props.albumId) return
  router.push(props.returnUrl)
}

const handleMessage = (event: MessageEvent) => {
  if (event.origin !== 'https://video-cdn.gov-murman.ru') {
    return
  }

  const response = JSON.parse(event.data)
  if (response.type === 'resources') {
    resource.value = response.data
    initMeta()
  }
}

const sendReaction = async (type: Reaction) => {
  if (props.albumId && props.videoId) {
    const path = `/api/albums/${props.albumId}/videos/${props.videoId}/react`
    setReaction(path, type, 'albums')
  }
}

const initMeta = () => {
  meta = useHead({
    title: video.value.title || 'Смотри Заполярье',
    meta: [
      { property: 'og:video:title', content: video.value.title },
      {
        property: 'og:video:description',
        content: video.value.description,
      },
      {
        property: 'og:video:duration',
        content: resource.value.duration,
      },
      {
        property: 'og:video',
        content: `https://video-cdn.gov-murman.ru/${video.value.external_id}`,
      },
    ],
  })
}

const clearMeta = () => meta?.dispose()

onMounted(() => {
  window.addEventListener('message', handleMessage)
  setTimeout(() => (href.value = window.location.href), 100)
})

onUnmounted(() => {
  clearRouteParams()
  clearMeta()
  window.removeEventListener('message', handleMessage)
})
</script>
